/**
 * Accordion + synced imagery (Rodewald-style).
 * Root: [data-accordion-list] with optional data-duration (ms, default 5000) for autoplay + progress.
 * Hover over the list column pauses autoplay and progress; media keeps cycling. Items: [data-accordion-item] + data-image.
 */
export function initAccordionContent(loco) {
  const root = document.querySelector("[data-accordion-list]");
  if (!root) return;

  const items = [...root.querySelectorAll("[data-accordion-item]")];
  const img = root.querySelector("[data-accordion-media-img]");
  const contentList = root.querySelector(".accordion-list__list");
  if (!items.length) return;

  const durationMs = Math.max(1000, parseInt(root.dataset.duration, 10) || 5000);
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  let elapsed = 0;
  let paused = false;
  let lastNow = 0;
  let rafId = 0;

  const bumpLoco = () => loco?.bump?.(520);

  const syncProgressBars = (ratio) => {
    const p = Math.max(0, Math.min(1, ratio));
    items.forEach((item) => {
      const bar = item.querySelector(".accordion-list__progress-bar");
      if (!bar) return;
      const v = item.classList.contains("is-active") ? p : 0;
      bar.style.setProperty("--ac-progress", String(v));
    });
  };

  const runImageTransition = (src, alt) => {
    if (!img || !src) return;
    const applyAnim = () => {
      img.classList.remove("is-entering");
      void img.offsetWidth;
      img.classList.add("is-entering");
      img.addEventListener(
        "animationend",
        () => {
          img.classList.remove("is-entering");
        },
        { once: true },
      );
      bumpLoco();
    };

    const sameUrl = (a, b) => {
      try {
        return new URL(a, window.location.href).href === new URL(b, window.location.href).href;
      } catch {
        return a === b;
      }
    };

    if (img.src && sameUrl(img.src, src)) {
      applyAnim();
      return;
    }

    const loader = new Image();
    loader.onload = () => {
      img.src = src;
      img.alt = alt || "";
      applyAnim();
    };
    loader.onerror = () => {
      img.alt = alt || "";
      applyAnim();
    };
    loader.src = src;
  };

  const activate = (target) => {
    items.forEach((item) => {
      const open = item === target;
      item.classList.toggle("is-active", open);
      const btn = item.querySelector("[data-accordion-trigger]");
      if (btn) btn.setAttribute("aria-expanded", open ? "true" : "false");
      const panel = item.querySelector(".accordion-list__panel");
      if (panel) panel.setAttribute("aria-hidden", open ? "false" : "true");
    });

    const src = target.dataset.image;
    const alt = target.dataset.imageAlt || "";
    runImageTransition(src, alt);
    bumpLoco();

    elapsed = 0;
    if (reduceMotion) {
      syncProgressBars(1);
    } else {
      syncProgressBars(0);
    }
  };

  items.forEach((item) => {
    const btn = item.querySelector("[data-accordion-trigger]");
    if (!btn) return;

    btn.addEventListener("click", () => {
      if (item.classList.contains("is-active")) return;
      activate(item);
    });
  });

  if (reduceMotion) {
    const active = items.find((i) => i.classList.contains("is-active")) || items[0];
    if (active && !active.classList.contains("is-active")) activate(active);
    syncProgressBars(1);
    return;
  }

  const tick = (now) => {
    rafId = requestAnimationFrame(tick);
    if (!lastNow) lastNow = now;
    const dt = now - lastNow;
    lastNow = now;

    if (paused) return;

    elapsed += dt;
    const p = Math.min(1, elapsed / durationMs);
    syncProgressBars(p);

    if (elapsed >= durationMs) {
      elapsed = 0;
      const idx = items.findIndex((i) => i.classList.contains("is-active"));
      const i = idx >= 0 ? idx : 0;
      const next = items[(i + 1) % items.length];
      activate(next);
    }
  };

  if (contentList) {
    contentList.addEventListener("pointerenter", () => {
      paused = true;
    });
    contentList.addEventListener("pointerleave", () => {
      paused = false;
      lastNow = 0;
    });
  }

  syncProgressBars(0);
  rafId = requestAnimationFrame(tick);
}
